import React from 'react';
import { DollarSign, Calendar, CheckCircle2, Clock, AlertCircle, FileText } from 'lucide-react';

const AdditionalFeesSection = ({ caseData, portalType = 'individual' }) => {
  const primaryColor = portalType === 'business' ? '#1E3A8A' : '#10B981';
  const getStatusIcon = (status) => {
    switch (status) {
      case 'مدفوعة':
        return <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2.5} />;
      case 'مستحقة':
        return <Clock className="w-6 h-6 text-white" strokeWidth={2.5} />;
      default:
        return <AlertCircle className="w-6 h-6 text-white" strokeWidth={2.5} />;
    }
  };

  // لا تظهر هذا القسم إذا لم تكن هناك رسوم إضافية
  if (!caseData.additionalFees || caseData.additionalFees.length === 0) {
    return null;
  }

  const totalFees = caseData.additionalFees.reduce((sum, fee) => sum + fee.amount, 0);
  const paidFees = caseData.additionalFees
    .filter(fee => fee.status === 'مدفوعة')
    .reduce((sum, fee) => sum + fee.amount, 0);
  const pendingFees = totalFees - paidFees;

  return (
    <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] rounded-xl flex items-center justify-center">
          <DollarSign className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h2 className="text-3xl font-black  style={{ color: primaryColor }}">الرسوم الإضافية</h2>
      </div>

      {/* ملخص الرسوم */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100 rounded-xl p-5">
          <div className="text-sm text-gray-600 font-bold mb-1">إجمالي الرسوم</div>
          <div className="text-3xl font-black  style={{ color: primaryColor }}">{totalFees.toLocaleString()} ريال</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 rounded-xl p-5">
          <div className="text-sm text-gray-600 font-bold mb-1">الرسوم المدفوعة</div>
          <div className="text-3xl font-black text-emerald-600">{paidFees.toLocaleString()} ريال</div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-100 rounded-xl p-5">
          <div className="text-sm text-gray-600 font-bold mb-1">الرسوم المستحقة</div>
          <div className="text-3xl font-black text-amber-600">{pendingFees.toLocaleString()} ريال</div>
        </div>
      </div>

      {/* قائمة الرسوم */}
      <div className="space-y-6">
        {caseData.additionalFees.map((fee) => (
          <div 
            key={fee.id}
            className={`border-2 rounded-xl p-6 transition-all hover:shadow-lg ${
              fee.status === 'مستحقة' 
                ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-white' 
                : 'border-gray-200 bg-white'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `linear-gradient(to bottom right, ${fee.statusColor}, ${fee.statusColor}dd)` }}
                >
                  {getStatusIcon(fee.status)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-black text-gray-900">{fee.type}</h3>
                  <p className="text-gray-600 text-sm mt-1">{fee.description}</p>
                </div>
              </div>
              <div className="text-left">
                <div className="text-3xl font-black  style={{ color: primaryColor }} mb-1">{fee.amount.toLocaleString()}</div>
                <div className="text-sm text-gray-500 font-semibold">{fee.currency}</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4">
                <span 
                  className="px-4 py-2 rounded-full text-sm font-bold"
                  style={{ 
                    backgroundColor: `${fee.statusColor}20`,
                    color: fee.statusColor
                  }}
                >
                  {fee.status}
                </span>
                {fee.invoiceNumber && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm font-semibold">{fee.invoiceNumber}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  {fee.status === 'مدفوعة' 
                    ? `تم الدفع: ${fee.paidDate}` 
                    : `مستحق: ${fee.dueDate}`
                  }
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ملاحظة */}
      {pendingFees > 0 && (
        <div className="mt-6 bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-black text-amber-900 mb-1">ملاحظة مهمة</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                هناك رسوم إضافية مستحقة بقيمة <span className="font-black text-amber-700">{pendingFees.toLocaleString()} ريال</span>. يرجى مراجعة التفاصيل أعلاه والتواصل مع فريق وازن للدفع.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalFeesSection;